"use strict";
export class DependentDropdownHandling {
  constructor({
    dependentDropdownMap,
    formId,
    formElement
  } = {}) {
    this.dependentDropdownMap = dependentDropdownMap ?? {};
    this._childrenByParent = this._buildChildrenByParent();
    this._root = formElement ?? (formId ? document.querySelector(`hi-form#${formId}`) : null);
    this._forms = /* @__PURE__ */ new Set();
    this._valueChangedHandlers = /* @__PURE__ */ new Map();
  }
  init() {
    if (!this._root) return;
    this._forms.add(this._root);
    this._refreshAll();
    this._forms.forEach((form) => {
      const handler = (event) => {
        const customEvent = event;
        if (!customEvent.detail?.originElement) return;
        const originNodeId = customEvent.detail.originElement.dataset?.nodeId;
        if (!originNodeId || !this._childrenByParent[originNodeId]) return;
        this._refreshChildrenOf(originNodeId);
      };
      this._valueChangedHandlers.set(form, handler);
      form.addEventListener("value-changed", handler);
    });
  }
  destroy() {
    this._forms.forEach((form) => {
      const handler = this._valueChangedHandlers.get(form);
      if (handler) form.removeEventListener("value-changed", handler);
    });
    this._forms.clear();
    this._valueChangedHandlers.clear();
  }
  _buildChildrenByParent() {
    const childrenByParent = {};
    Object.entries(this.dependentDropdownMap).forEach(([childNodeId, config]) => {
      const parentNodeId = config.controlling_element_id;
      if (!parentNodeId) return;
      if (!childrenByParent[parentNodeId]) childrenByParent[parentNodeId] = [];
      childrenByParent[parentNodeId].push({
        childNodeId,
        optionMapping: config.option_mapping ?? {},
        defaultOptions: config.default_options ?? []
      });
    });
    return childrenByParent;
  }
  _refreshAll() {
    const childNodeIds = new Set(Object.keys(this.dependentDropdownMap));
    const rootParentIds = [
      ...new Set(
        Object.values(this.dependentDropdownMap).map((config) => config.controlling_element_id).filter((id) => !!id && !childNodeIds.has(id))
      )
    ];
    if (rootParentIds.length > 0) {
      rootParentIds.forEach((parentNodeId) => this._refreshChildrenOf(parentNodeId));
      return;
    }
    const parentNodeIds = [
      ...new Set(
        Object.values(this.dependentDropdownMap).map((config) => config.controlling_element_id).filter((id) => Boolean(id))
      )
    ];
    parentNodeIds.forEach((parentNodeId) => this._refreshChildrenOf(parentNodeId));
  }
  _refreshChildrenOf(parentNodeId, visited = /* @__PURE__ */ new Set()) {
    if (visited.has(parentNodeId)) return;
    visited.add(parentNodeId);
    const children = this._childrenByParent[parentNodeId] ?? [];
    const parentElement = this._queryWithinRoot(`[data-node-id="${parentNodeId}"]`);
    if (!parentElement) return;
    const parentValue = this._getElementValue(parentElement);
    children.forEach(({ childNodeId, optionMapping, defaultOptions }) => {
      this._filterChildOptions(childNodeId, parentValue, optionMapping, defaultOptions);
      this._refreshChildrenOf(childNodeId, visited);
    });
  }
  _queryWithinRoot(selector) {
    if (!this._root) return null;
    return this._root.querySelector(selector);
  }
  _getElementValue(element) {
    const el = element;
    if (element.tagName === "HI-COMBOBOX") {
      return el.multiple ? el.values : el.value;
    }
    return element.value;
  }
  _filterChildOptions(childNodeId, parentValue, optionMapping, defaultOptions = []) {
    const childElement = this._queryWithinRoot(
      `[data-node-id="${childNodeId}"]`
    );
    if (!childElement) return;
    const parentValueStr = Array.isArray(parentValue) ? parentValue[0] : parentValue;
    let filteredOptions;
    if (!parentValueStr) {
      filteredOptions = [];
    } else if (Object.prototype.hasOwnProperty.call(optionMapping, parentValueStr)) {
      filteredOptions = (optionMapping[parentValueStr] ?? []).map((v) => ({ title: v, value: v }));
    } else {
      filteredOptions = defaultOptions.map((v) => ({ title: v, value: v }));
    }
    childElement.input = filteredOptions;
    const effectiveAllowed = filteredOptions.map((o) => o.value);
    const currentValues = childElement.multiple ? childElement.values ?? [] : [childElement.value];
    const invalidValues = currentValues.filter((v) => v && !effectiveAllowed.includes(v));
    if (invalidValues.length > 0) {
      if (childElement.multiple) {
        childElement.values = currentValues.filter((v) => effectiveAllowed.includes(v));
      } else {
        childElement.clear?.();
      }
    }
  }
}
